import type { CapacityContributionType, GarageAvailableSlotRecord } from '@navio/nsdk';
import {
  addDays,
  collapseSlotsByWallClock,
  CustomDateFormats,
  getStartOfWeek,
  groupCapacityUiSlotsByDate,
  noddiFormatDate,
  slotCapacityFromEmbeddedSupply,
  slotSupplyKey,
  type SlotCapacityInfo
} from 'noddi-util';
import type { DayWithSlots, TimeSlot } from './types';

type AvailableSlotInput = {
  start: string;
  end: string;
  workstationId?: number | null;
  contributionCount?: number | null;
  baselineContributionCount?: number | null;
  contributions?: Array<{
    id: number;
    name: string;
    capabilityNames: string[];
    contributionType?: CapacityContributionType;
  }> | null;
};

export type NormalizedCapacitySlot = {
  id: string;
  start: string;
  end: string;
  label: string;
  workstationId?: number | null;
  /** Bay count after wall-clock collapse (1 when single bay). */
  availableCount: number;
  capacity?: SlotCapacityInfo;
};

/** Merge public slot rows with optional named contribution supply (detailed mode). */
export function mergeCapacitySlotsWithContributions(
  slots: GarageAvailableSlotRecord[],
  contributionSlots: GarageAvailableSlotRecord[] | undefined,
  detailed: boolean
): GarageAvailableSlotRecord[] {
  if (!detailed || slots.length === 0) {
    return slots;
  }

  const contribByKey = new Map<string, GarageAvailableSlotRecord>();
  for (const slot of contributionSlots ?? []) {
    contribByKey.set(slotSupplyKey(slot), slot);
  }

  if (contribByKey.size === 0) {
    return slots;
  }

  return slots.map((slot) => contribByKey.get(slotSupplyKey(slot)) ?? slot);
}

/** A window the customer is holding; the bay it sits on is already theirs. */
export type HeldSlotWindow = {
  start: string;
  end: string;
  workstationId?: number | null;
};

/**
 * Keep the customer's own held window on the grid. Their hold consumes the bay, so
 * the next capacity read stops listing it as free and the selected tile would
 * disappear from under them on the first refresh.
 */
export const withHeldWindow = (
  slots: GarageAvailableSlotRecord[],
  heldWindow: HeldSlotWindow | null | undefined
): GarageAvailableSlotRecord[] => {
  if (!heldWindow || slots.some((slot) => slot.start === heldWindow.start && slot.end === heldWindow.end)) {
    return slots;
  }
  return [...slots, { start: heldWindow.start, end: heldWindow.end, workstationId: heldWindow.workstationId ?? null }];
};

/** Match a held/selected wall-clock window to a collapsed tile id. */
export const findSlotIdForTime = (
  slots: Pick<NormalizedCapacitySlot, 'id' | 'start' | 'end'>[],
  selected: { startsAt: string; endsAt: string } | null | undefined
): string | null => {
  if (!selected) {
    return null;
  }
  return slots.find((s) => s.start === selected.startsAt && s.end === selected.endsAt)?.id ?? null;
};

/** Inclusive calendar day range starting at `from` for `dayCount` days. */
export const getDateRange = (from: Date, dayCount: number): Date[] => {
  const dates: Date[] = [];
  // eslint-disable-next-line no-restricted-syntax -- indexed loop counter over dayCount days
  for (let i = 0; i < dayCount; i++) {
    const nextDay = addDays(from, i);
    if (nextDay) {
      dates.push(nextDay);
    }
  }
  return dates;
};

/** Mon–Sat week (6 days) used by customer stationary capacity navigation. */
export const getWeekDates = (date: Date) => getDateRange(getStartOfWeek(date), 6);

export type CapacitySlotLabelMode = 'start-only' | 'start-end';

/** Normalize API rows: sort, collapse multi-bay windows, pin first bay. */
export const normalizeAvailableSlots = (
  availableSlots: AvailableSlotInput[],
  toZoned: (value: string) => Date,
  labelMode: CapacitySlotLabelMode = 'start-only'
): NormalizedCapacitySlot[] => {
  const sorted = [...availableSlots].sort((a, b) => a.start.localeCompare(b.start));

  const collapsible = sorted.map((slot) => {
    const capacity = slotCapacityFromEmbeddedSupply(slot);
    const hasCapacity = capacity.personalCount > 0 || capacity.baselineCount > 0 || capacity.contributions.length > 0;

    return {
      start: slot.start,
      end: slot.end,
      workstationId: slot.workstationId,
      capacity: hasCapacity ? capacity : undefined
    };
  });

  return collapseSlotsByWallClock(collapsible).map((tile) => {
    const startLabel = noddiFormatDate(toZoned(tile.start), CustomDateFormats.TIME);
    const label =
      labelMode === 'start-end'
        ? `${startLabel} – ${noddiFormatDate(toZoned(tile.end), CustomDateFormats.TIME)}`
        : startLabel;

    return {
      id: tile.id,
      start: tile.start,
      end: tile.end,
      label,
      workstationId: tile.pinned.workstationId,
      availableCount: tile.availableCount,
      capacity: tile.capacity
    };
  });
};

/** Group collapsed wall-clock tiles into calendar days for the picker UI. */
export const groupSlotsIntoDays = (
  slots: NormalizedCapacitySlot[],
  weekDatesInRange: Date[] | undefined,
  toZoned: (value: string) => Date,
  totalWorkstations?: number
): { days: DayWithSlots[]; slotLookup: Map<string, NormalizedCapacitySlot> } => {
  const slotLookup = new Map<string, NormalizedCapacitySlot>();

  if (slots.length === 0) {
    return { days: [], slotLookup };
  }

  const uiSlots = slots.map((slot) => {
    slotLookup.set(slot.id, slot);
    return {
      id: slot.id,
      label: slot.label,
      availableCount: slot.availableCount,
      capacity: slot.capacity,
      dateKey: noddiFormatDate(toZoned(slot.start), CustomDateFormats.NO_DASHED_DATE_ISO_8601_SHORT)
    };
  });

  const orderedDateKeys = weekDatesInRange?.map((date) =>
    noddiFormatDate(date, CustomDateFormats.NO_DASHED_DATE_ISO_8601_SHORT)
  );
  const dateLabelByKey = new Map(
    (weekDatesInRange ?? []).map((date) => [
      noddiFormatDate(date, CustomDateFormats.NO_DASHED_DATE_ISO_8601_SHORT),
      noddiFormatDate(date, CustomDateFormats.FULL_DAY_MONTH)
    ])
  );

  const days: DayWithSlots[] = groupCapacityUiSlotsByDate(uiSlots, {
    totalWorkstations,
    orderedDateKeys
  }).map((day) => ({
    dateKey: day.dateKey,
    dateLabel: dateLabelByKey.get(day.dateKey) ?? noddiFormatDate(day.dateKey, CustomDateFormats.FULL_DAY_MONTH),
    slots: day.slots.map(
      (s): TimeSlot => ({
        id: s.id,
        label: s.label,
        availableCount: s.availableCount,
        totalWorkstations: s.totalWorkstations,
        capacity: s.capacity
      })
    )
  }));

  return { days, slotLookup };
};
